import type { ConversationBoardDto } from '../../api/types';

export type BoardItem = ConversationBoardDto['items'][number];
export type BoardState = BoardItem['state'];

export const STATE_LABEL: Record<BoardState, string> = { pendente: 'Trabalho restante', em_andamento: 'Em andamento', no_produto: 'No Produto', concluido: 'Concluído', bloqueado: 'Impedimento' };
export const TARGET_LABEL = { analysis: 'Análise', implementation: 'Implementação', production: 'Produção' };
export const BLOCKER_LABEL: Record<string, string> = { '2fa': 'código/2FA seu', payment: 'pagamento seu', product_decision: 'decisão de produto sua', owner_evidence: 'evidência que só você tem', external_party: 'terceiro', technical: 'causa técnica' };
export const OPEN: BoardState[] = ['pendente', 'em_andamento', 'no_produto', 'bloqueado'];

export function when(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export type RowTone = 'owner' | 'active' | 'product' | 'done' | 'blocked' | 'waiting' | 'superseded';

/** Estado da linha em uma palavra, com o tom que pinta o marcador (o texto nunca depende só da cor). */
export function rowStatus(item: BoardItem): { label: string; tone: RowTone } {
  if (item.supersededBy) return { label: 'Substituída', tone: 'superseded' };
  if (item.state === 'concluido') return { label: 'Concluído', tone: 'done' };
  if (item.owner === 'dono') return { label: 'Sua vez', tone: 'owner' };
  if (item.state === 'em_andamento') return { label: 'Em andamento', tone: 'active' };
  if (item.state === 'no_produto') return { label: 'No Produto', tone: 'product' };
  if (item.delivery?.blockerOwner === 'external') return { label: 'Aguarda condição externa', tone: 'waiting' };
  if (item.state === 'bloqueado') return { label: 'Impedimento', tone: 'blocked' };
  return { label: 'Trabalho restante', tone: 'waiting' };
}

/** Quando: a próxima conferência (futura, sem conferência em voo) ou a última atualização. */
export function rowWhen(item: BoardItem, now = Date.now()): string {
  if (item.state !== 'concluido' && item.continuation?.basis === 'linked_tasks') return 'aguarda tarefas vinculadas';
  const review = item.review;
  const inFlight = review && ['opening', 'checking', 'uncertain'].includes(review.state);
  if (item.state !== 'concluido' && review?.nextCheckAt && !inFlight && Date.parse(review.nextCheckAt) > now) return `confere ${when(review.nextCheckAt)}`;
  if (inFlight) return review?.state === 'checking' ? 'conferindo agora' : review?.state === 'opening' ? 'abrindo o terminal' : 'confirmando';
  return `atualizado ${when(item.updatedAt)}`;
}

/** Quem está com o apontamento, quando isso diz algo além do projeto. */
export function informativeOwner(item: BoardItem): string | null {
  const label = item.ownerLabel?.trim();
  if (!label || label === 'Depende de você' || label === item.project) return null;
  return label;
}
